// material.ts — (B112; planning 10-24 19:20, 20:20, 20:50; Creative 2's C2.5-4) the final review's list of material changes,
// pure: the answer of GET /api/videos/:id/material, where each clip starts in the player, and the videos list's badge.
// The list is read, never decided (approving stays the gate's own decision); its words are shown as the store gives them.
import type { MaterialList } from "@vms/contracts";   // a type only: erased at build
import { w } from "./words.ts";

/** The backend's answer: the list, or not readable with why (BEATS_UNREADABLE: the clips couldn't be read). */
export type MaterialAnswer = ({ readable: true } & MaterialList) | { readable: false; code: string };

/** Pure: where each beat starts in the finished video, in seconds — the conformed timeline's start (the filmstrip's
 *  `production.conformedStartMs`), only where the engine gives one: a beat without it gets no link, never a guessed moment. */
export function clipStarts(beats: readonly { beatId: string; production?: { conformedStartMs?: number | null } | null }[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const b of beats) {
    const ms = b.production?.conformedStartMs;
    if (typeof ms === "number" && Number.isFinite(ms) && ms >= 0) out.set(b.beatId, ms / 1000);
  }
  return out;
}

/** Pure: what the final review says, in order — the items (each its heading, sentence, clip and where it starts), or
 *  "Everything was made as planned", or why the list isn't shown. */
export type MaterialShown =
  | { kind: "items"; items: { key: string; heading: string; sentence: string; clipLabel: string; startSec: number | null }[] }
  | { kind: "as_planned"; line: string }
  | { kind: "unreadable"; line: string };
export function materialShown(a: MaterialAnswer | null, failed: boolean, starts: ReadonlyMap<string, number>): MaterialShown | null {
  if (failed) return { kind: "unreadable", line: w("page.material.not_read") };
  if (!a) return null;   // still loading
  if (!a.readable) return { kind: "unreadable", line: a.code === "BEATS_UNREADABLE" ? w("page.material.clips_unreadable") : w("page.material.not_read") };
  if (a.nothingChanged || a.items.length === 0) return { kind: "as_planned", line: w("page.material.as_planned") };
  return { kind: "items", items: a.items.map((i) => ({ key: `${i.item}|${i.beatId ?? ""}`, heading: i.heading, sentence: i.sentence,
    clipLabel: i.clip === null ? w("page.material.whole_video") : w("page.material.clip", { n: i.clip }),
    startSec: i.beatId === null ? null : starts.get(i.beatId) ?? null })) };
}

/** Pure: the videos list's badge beside "Ready": NULL → none (as before); 0 → "as planned"; else the count. */
export function materialBadge(count: number | null | undefined): string | null {
  if (count === null || count === undefined || !Number.isInteger(count) || count < 0) return null;
  return count === 0 ? w("page.material.badge_as_planned") : count === 1 ? w("page.material.badge_one") : w("page.material.badge_many", { n: count });
}

/** Pure: the status word with the badge after it ("Ready · 3 changes"); no badge: the word as it was (undefined: the pill's own). */
export function withMaterial(word: string | undefined, badge: string | null): string | undefined {
  return badge && word ? `${word} · ${badge}` : word;
}
